import DataMap from 'assets/components/dataMap/DataMap';
import useApi from 'assets/hooks/api/useApi';
import { amexCard, masterCard, visaCard } from 'assets/themes/assets/Images';
import { defaultDateFormat, localizeDate } from 'assets/utils/data/Date';
import { combineStrings } from 'assets/utils/data/String';
import { isEmpty } from 'lodash';
import ProductType from 'models/enums/ProductType';
import DocumentTranslation from 'models/localization/documentTranslation/Model.DocumentTranslation';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router';
import { useReactToPrint } from 'react-to-print';
import en from '../../../../../assets/locales/languages/en';
import fr from '../../../../../assets/locales/languages/fr';
import { useStatementBalanceProvider } from '../StatementBalance.Hooks';
import '../style/print.scss';

export default function StatementBalancePrint() {
  const { selected, statementBalanceContractsPerMonths, getContractsPaymentAmounts } = useStatementBalanceProvider();
  const history = useHistory();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const locales = { en, fr };
  const language = selected?.dealer?.documentLanguageIsoCode || 'en';
  const lang = locales[language];

  const providerLogoTranslationsListApi = useApi(
    {
      action: DocumentTranslation.list,
      body: { documentType: 'ProviderLogo', documentId: [selected?.dealer?.provider?.logo?.id] },
      wait: !selected,
    },
    [selected]
  );
  const providerLogoUrl = useMemo(() => {
    const logos: { languageIsoCode: string; mediaFile: Model.IMediaFile }[] = [];
    logos.push({
      languageIsoCode: selected?.dealer?.provider?.logo?.languageIsoCode,
      mediaFile: selected?.dealer?.provider?.logo?.mediaFile,
    });

    if (!isEmpty(providerLogoTranslationsListApi?.payload?.data)) {
      providerLogoTranslationsListApi?.payload?.data?.map((it) => {
        if (!it) return;
        logos.push({ languageIsoCode: it.languageIsoCode, mediaFile: it.mediaFile });
      });
    }

    return logos.find((it) => it.languageIsoCode === language)?.mediaFile?.url;
  }, [language, providerLogoTranslationsListApi?.payload?.data]);

  const handlePrint = useReactToPrint({
    content: () => componentRef?.current,
    documentTitle: `${lang.statementBalancePdf}#${selected?.statementBalanceNumber}`,
  });

  useEffect(() => {
    if (providerLogoUrl) {
      handlePrint();
      history.goBack();
    }
  }, [providerLogoUrl]);

  return !selected ? null : (
    <div id="printStatementBalance" ref={componentRef}>
      <style>{`@page {size: a4 portret; margin: 1cm;}`}</style>
      <h1 style={{ fontSize: '4rem', textAlign: 'end' }}>
        {lang.statementBalancePdf} #{selected.statementBalanceNumber}
      </h1>
      <div className="print_details">
        <div className="print_details_company">
          <img src={providerLogoUrl} />
          <p>
            {selected.dealer?.provider && (
              <>
                <b>{selected.dealer?.provider?.name},</b>
                <br />
                {selected.dealer?.provider?.streetAddress}, {selected.dealer?.provider?.postalCode}
                <br />
                {selected.dealer?.provider?.city}, {selected.dealer?.provider?.provinceCode}{' '}
                {selected.dealer?.provider?.countryCode}
              </>
            )}
          </p>
        </div>
        <div className="print_details_dealer">
          <p>
            <b>{lang.date}: </b>
            {localizeDate(selected.createdAtLocal, defaultDateFormat, language)}
            <br />
            <b>{lang.dealer}: </b>
            {selected.dealer?.name}
            <br />
            <b>{lang.address}: </b>
            <br />
            {selected.dealer?.streetAddress}, {selected.dealer?.postalCode}
            <br />
            {selected.dealer?.city}, {selected.dealer?.provinceCode}, {selected.dealer?.countryCode}
          </p>
          <p>
            {lang.statementP1}
            <br />
            {lang.statementP2}:
            <br />
            {selected.dealer?.provider && <b>{selected.dealer?.provider?.name}</b>}
          </p>
          <div className="print_details_dealer_cards">
            <img src={visaCard} />
            <img src={masterCard} />
            <img src={amexCard} />
          </div>
        </div>
      </div>
      {/*Statement Balance due section*/}
      <div className="print_table_header">
        <table className="print_table_header_title">
          <tr>
            <th>{lang.balanceDueIsPayableUponReceipt}</th>
          </tr>
        </table>
        <table className="print_table_header_payments">
          <tr>
            <th>{lang.balanceDue}</th>
            {statementBalanceContractsPerMonths?.map((it) => (
              <th key={it.label}>{lang[it.label]}:</th>
            ))}
          </tr>
          <tr>
            <td>${getContractsPaymentAmounts(selected?.contracts).paymentAmountWithTaxCAD.toFixed(2)}</td>
            {statementBalanceContractsPerMonths?.map((it) => (
              <td key={it.label}>${it.paymentAmountWithTaxCAD}</td>
            ))}
          </tr>
        </table>
        <div className="print_table_header_warning">
          <p>
            {lang.statementBalanceRemit} <a href="mailto:remittances@goautoshield.com">remittances@goautoshield.com</a>
          </p>
          <div className="print_table_header_warning_box">
            <p>
              <b>{lang.important.toLocaleUpperCase()}:</b> {lang.statementBalanceImportantNotice}
            </p>
          </div>
        </div>
      </div>
      {/*Contract Summary section*/}
      <div className="print_table_body">
        <table>
          <DataMap
            data={statementBalanceContractsPerMonths}
            render={({ data: statementContractGroup }) => (
              <Fragment key={statementContractGroup.label}>
                {statementContractGroup.label && (
                  <>
                    <tr
                      className={
                        statementContractGroup.label === 'sixtyOneTo90Days' ||
                        statementContractGroup.label === 'ninetyOnePlusDays'
                          ? 'print_title_row_warning'
                          : 'print_title_row'
                      }
                    >
                      <td colSpan={10}>{lang[statementContractGroup.label]}</td>
                    </tr>

                    {Object.entries(statementContractGroup.productContractsDict).map(([productKey, contracts]) => {
                      return (
                        <Fragment key={productKey}>
                          <tr className="print_background_color">
                            <th style={{ width: '8%' }}>
                              {lang.app}
                              {selected?.dealer?.documentLanguageIsoCode != 'en' ? '' : '#'}
                            </th>
                            <th>{lang.product}</th>
                            <th style={{ width: '8%' }}>
                              {lang.stock}
                              {selected?.dealer?.documentLanguageIsoCode != 'en' ? '' : '#'}
                            </th>
                            <th style={{ width: '20%' }}>{lang.coverage}</th>
                            <th>{lang.client}</th>
                            <th style={{ width: '10%' }}>{lang.created}</th>
                            <th style={{ width: '10%' }}>{lang.statementEffectiveOn}</th>
                            <th className="th_payment">{lang.amount}</th>
                            <th className="th_payment">{lang.tax}</th>
                            <th className="th_payment">{lang.total}</th>
                          </tr>
                          <DataMap
                            data={contracts}
                            render={({ data: contract }) => (
                              <Fragment key={contract.id}>
                                <tr>
                                  <td>{contract.appNumberWithRevision}</td>
                                  <td>{combineStrings(' ', productKey)}</td>
                                  <td>{contract.vehicle?.stockNumber || 'n/a'}</td>
                                  <td>
                                    {contract.product.productType === ProductType.warrantyTierBased && (
                                      <div>
                                        <b>{lang.term}:</b> {contract.termInMonths || 'n/a'}m
                                        <br />
                                        <b>{lang.tier}:</b> {contract.tier || 'n/a'}
                                      </div>
                                    )}
                                    {contract.product.productType === ProductType.nonWarranty && (
                                      <div>
                                        <b>{lang.term}:</b> {contract.termInMonths || 'n/a'}m
                                        <br />
                                        <b>{lang.price}:</b>
                                        {`$${contract.minVehiclePriceCAD.toFixed(2) || 'n/a'} - $${
                                          contract.maxVehiclePriceCAD.toFixed(0) || 'n/a'
                                        }`}
                                      </div>
                                    )}
                                    {contract.product.productType === ProductType.warrantiClassBased && (
                                      <div>
                                        <b>{lang.term}:</b> {contract.termInMonths || 'n/a'}m /{' '}
                                        {contract.maxKm || 'n/a'}km
                                        <br />
                                        <b>{lang.coverage}:</b> {contract.customInfo || 'n/a'}
                                      </div>
                                    )}
                                    {contract.product.productType === ProductType.custom && (
                                      <div>
                                        <b>{lang.term}:</b> {contract.termInMonths || 'n/a'}m
                                        <br />
                                        <b>{lang.coverage}:</b> {contract.customInfo || 'n/a'}
                                        <br />
                                        <b>{lang.coverageType}:</b> {contract.customInfoGroup || 'n/a'}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {combineStrings(' ', contract.client?.firstName, contract.client?.lastName)}
                                    <br />
                                    {`(...${contract.vehicle?.vin.toString().slice(-8)})`}
                                  </td>
                                  <td>{localizeDate(contract?.createdAtLocal, defaultDateFormat, language)}</td>
                                  <td>{localizeDate(contract?.effectiveDate, defaultDateFormat, language)}</td>
                                  <td className="td_payment">{`$${contract.paymentAmountCAD.toFixed(2)}`}</td>
                                  <td className="td_payment">{`$${contract.paymentTaxCAD.toFixed(2)}`}</td>
                                  <td className="td_payment">{`$${contract.paymentAmountWithTaxCAD.toFixed(2)}`}</td>
                                </tr>
                              </Fragment>
                            )}
                          />
                          {contracts?.length > 1 && (
                            <>
                              <tr className="print_background_color print_never_break">
                                <td colSpan={10}></td>
                              </tr>
                              <tr className="print_never_break">
                                <td colSpan={6}></td>
                                <td>
                                  <b>{lang.subtotal.toLocaleUpperCase()}</b>
                                </td>
                                <td className="td_payment">
                                  <b>{`$${getContractsPaymentAmounts(contracts).paymentAmountCAD.toFixed(2)}`}</b>
                                </td>
                                <td colSpan={2}></td>
                              </tr>
                              <tr className="print_never_break">
                                <td>
                                  <b>{lang.numberOfApps.toLocaleUpperCase()} </b>
                                </td>
                                <td className="td_payment">
                                  <b>{contracts?.length}</b>
                                </td>
                                <td colSpan={5}></td>
                                <td>
                                  <b>{lang.statementSalesTax.toLocaleUpperCase()}</b>
                                </td>
                                <td className="td_payment">
                                  <b>{`$${getContractsPaymentAmounts(contracts).paymentTaxCAD.toFixed(2)}`}</b>
                                </td>
                                <td></td>
                              </tr>
                              <tr className="print_never_break">
                                <td colSpan={8}></td>
                                <td>
                                  <b>{lang.total.toLocaleUpperCase()}</b>
                                </td>
                                <td className="td_payment">
                                  <b>{`$${getContractsPaymentAmounts(contracts).paymentAmountWithTaxCAD.toFixed(
                                    2
                                  )}`}</b>
                                </td>
                              </tr>
                            </>
                          )}
                        </Fragment>
                      );
                    })}
                    {/*Total sum for each range*/}
                    <tr className="print_background_color print_never_break">
                      <td colSpan={10}></td>
                    </tr>
                    <tr className="print_never_break">
                      <td colSpan={6}></td>
                      <td>
                        <b>{lang.subtotal.toLocaleUpperCase()}</b>
                      </td>
                      <td className="td_payment">
                        <b>${statementContractGroup.paymentAmountCAD}</b>
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                    <tr className="print_never_break">
                      <td>
                        {lang.numberOfApps.toLocaleUpperCase()} {lang[statementContractGroup.label]}
                      </td>
                      <td className="td_payment">
                        <b>{statementContractGroup.length}</b>
                      </td>
                      <td colSpan={5}></td>
                      <td>
                        <b>{lang.statementSalesTax.toLocaleUpperCase()}</b>
                      </td>
                      <td className="td_payment">
                        <b>${statementContractGroup.paymentTaxCAD}</b>
                      </td>
                      <td></td>
                    </tr>
                    <tr className="print_never_break">
                      <td colSpan={8}></td>
                      <td>
                        <b>{lang.total.toLocaleUpperCase()}</b>
                      </td>
                      <td className="td_payment">
                        <b>${statementContractGroup.paymentAmountWithTaxCAD}</b>
                      </td>
                    </tr>
                  </>
                )}
                {(statementContractGroup.label === 'sixtyOneTo90Days' ||
                  statementContractGroup.label === 'ninetyOnePlusDays') && (
                  <tr>
                    <td colSpan={10}>* {lang.statementBalanceCancelationRisks}</td>
                  </tr>
                )}
              </Fragment>
            )}
          />

          {/*Total sum section*/}
          <tr className="print_title_row">
            <td colSpan={10}>{lang.totalSum.toLocaleUpperCase()}</td>
          </tr>
          <tr>
            <td colSpan={6}></td>
            <td>
              <b>{lang.subtotal.toLocaleUpperCase()}</b>
            </td>
            <td className="td_payment">
              <b>{`$${getContractsPaymentAmounts(selected?.contracts).paymentAmountCAD.toFixed(2)}`}</b>
            </td>
            <td colSpan={2}></td>
          </tr>
          <tr>
            <td>
              <b>{lang.numberOfApps.toLocaleUpperCase()}</b>
            </td>
            <td className="td_payment">
              <b>{selected?.contracts?.length}</b>
            </td>
            <td colSpan={5}></td>
            <td>
              <b>{lang.statementSalesTax.toLocaleUpperCase()}</b>
            </td>
            <td className="td_payment">
              <b>{`$${getContractsPaymentAmounts(selected?.contracts).paymentTaxCAD.toFixed(2)}`}</b>
            </td>
            <td></td>
          </tr>
          <tr>
            <td colSpan={8}></td>
            <td>
              <b>{lang.total.toLocaleUpperCase()}</b>
            </td>
            <td className="td_payment">
              <b>{`$${getContractsPaymentAmounts(selected?.contracts).paymentAmountWithTaxCAD.toFixed(2)}`}</b>
            </td>
          </tr>
        </table>
      </div>
    </div>
  );
}
